import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {
  BlockInDashboardSelectorQuery,
  BlockInDashboardSelectorQueryVariables,
} from './__generated/BlockInDashboardSelectorQuery';

type AnalyticsDashboard = ExtractByTypename<
  BlockInDashboardSelectorQuery['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsQuery = AnalyticsDashboard['layoutRows'][number]['analyticsQueries'][number];

export const BLOCK_IN_DASHBOARD_SELECTOR_QUERY = gql`
  query BlockInDashboardSelectorQuery($parentDashboardId: StrictString!)
  @skipAudit
  @engComponent(name: INSIGHTS) {
    analyticsDashboard(id: $parentDashboardId) {
      ... on AnalyticsDashboardNode {
        id
        layoutRows {
          analyticsQueries {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getAnalyticsQueriesInLayoutOrder(dashboard: AnalyticsDashboard): Array<AnalyticsQuery> {
  return dashboard.layoutRows.flatMap((row) => row.analyticsQueries);
}

function findAnalyticsQueryName(analyticsQueries: Array<AnalyticsQuery>, analyticsQueryId: string): string {
  return (
    analyticsQueries.find((analyticsQuery) => analyticsQuery.id === analyticsQueryId)?.name ?? 'Unknown block'
  );
}

export interface BlockInDashboardSelectorProps {
  parentDashboardId: string;
  selectedAnalyticsQueryId: string | null;
  onSelect: (analyticsQueryId: string) => void;
}

/**
 * Searchable dropdown for the user to select a block within a (parent) dashboard
 *
 * The dropdown displays block names. The blocks are listed in the dashboard's layout order.
 */
export default function BlockInDashboardSelector(props: BlockInDashboardSelectorProps): JSX.Element | null {
  const queryResult = useBenchlingQuery<
    BlockInDashboardSelectorQuery,
    BlockInDashboardSelectorQueryVariables
  >(BLOCK_IN_DASHBOARD_SELECTOR_QUERY, {variables: {parentDashboardId: props.parentDashboardId}});
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Unable to load dashboard. Please try again.');
    return null;
  }
  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  const dashboard = queryResult.data.analyticsDashboard;
  if (isInaccessible(dashboard)) {
    return (
      <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this dashboard." />
    );
  }

  const analyticsQueries = getAnalyticsQueriesInLayoutOrder(dashboard);
  return (
    <SelectDropdown
      menu={
        <SearchableMenu
          onSelect={props.onSelect}
          optionDatas={
            analyticsQueries.length > 0
              ? analyticsQueries.map((analyticsQuery) => ({
                  name: analyticsQuery.name,
                  value: analyticsQuery.id,
                }))
              : [{name: 'Dashboard has no blocks', disabled: true, value: '__NO_BLOCKS__'}]
          }
          selectedValue={props.selectedAnalyticsQueryId}
          width={Menu.WIDTHS.FULL}
        />
      }
      displayName={
        props.selectedAnalyticsQueryId != null
          ? findAnalyticsQueryName(analyticsQueries, props.selectedAnalyticsQueryId)
          : 'Select block...'
      }
      dataTestAttrs={{
        component: 'BlockInDashboardSelector',
        element: 'root',
      }}
    />
  );
}
